





















































































































































import Vue from "vue";
import { RootState } from "@/store";
import { mapActions, mapGetters, mapState } from "vuex";
import moment from "moment";

import { FETCH_EXCEPTION_IMAGE } from "@/store/modules/LogAnalysisModule/actions";
import { Machine } from "@/model/data/Machine";

export default Vue.extend({
    props: {
        loading: Boolean,
        processedCashList: Array,
        dateListString: Array,
        machineExceptionList: Array,
        troubleshooting: Object,
        unitLabelMap: Object,
        componentLabelMap: Object,
        imagesLegend: Map,
        startDate: Date,
        machine: Object as () => Machine,
        installationName: String,
        nDays: Number,
    },
    data() {
        return {
            showImageDialog: false,
            selectedException: undefined,
            loadingImage: false,
            exceptionTableHeader: [
                { text: this.$t("logAnalysis.exceptionCode"), value: "code" },
                { text: this.$t("logAnalysis.exceptionArea"), value: "area" },
                { text: this.$t("logAnalysis.exceptionComponent"), value: "component" },
                { text: this.$t("logAnalysis.exceptionDescription"), value: "description", sortable: false },
                { text: this.$t("logAnalysis.exceptionPercentage"), value: "percentage" },
                { text: this.$t("logAnalysis.exceptionCount"), value: "number" },
                { text: this.$t("logAnalysis.exceptionImage"), value: "image" },
            ],
            exceptionTableData: [],
            seriesMETrendList: [],
            totalRowExceptionDataTable: 0,
            maxYValue: 0,
            moment,
        };
    },
    computed: {
        ...mapState({
            userTheme: (state: RootState) => state.authMod.currentUser.userConfig.theme,
            cachedImages: (state: RootState) => state.logAnalysis.cachedImages,
        }),
        ...mapGetters(["longDateFormat"]),
        chartMachineExceptionsTrend() {
            return {
                options: {
                    theme: {
                        mode: this.userTheme,
                    },
                    chart: {
                        type: "bar",
                        stacked: true,
                        height: 350,
                        zoom: {
                            enabled: false,
                        },
                        animations: {
                            enabled: false,
                        },
                    },
                    plotOptions: {
                        bar: {
                            borderRadius: 4,
                            horizontal: false,
                        },
                    },
                    grid: {
                        xaxis: {
                            lines: {
                                show: false,
                            },
                        },
                    },
                    dataLabels: {
                        enabled: false,
                    },
                    xaxis: {
                        type: "category",
                        categories: this.dateListString,
                        tickPlacement: "on",
                    },
                    yaxis: {
                        labels: {
                            formatter: (val) => val.toFixed(0),
                        },
                        max: this.maxYValue + 1,
                    },
                },
                series: this.seriesMETrendList,
            };
        },
    },
    methods: {
        ...mapActions([FETCH_EXCEPTION_IMAGE]),
        setRowStyle() {
            return "style-1";
        },
        openImageDialog(value) {
            this.showImageDialog = true;
            let troubGuide = value.troub;
            let cause = this.$t("logAnalysis.cause");
            let solution = this.$t("logAnalysis.solution");
            let regExp = new RegExp(".*(" + cause + "|" + solution + ").*", "ig");
            let causeOrSolutionWords = troubGuide.match(regExp);
            if (causeOrSolutionWords != null)
                causeOrSolutionWords.forEach((word) => {
                    troubGuide = troubGuide.replace(word, "<strong>" + word + "</strong>");
                });
            this.selectedException = {
                exc: value.exc,
                image: "",
                areaDescr: value.area,
                codeDescr: value.code,
                compDescr: value.component,
                descr: value.description,
                procType: value.exc.processType != null ? this.$t("logAnalysis.processType_" + value.exc.processType) : "",
                troub: troubGuide,
                legend: value.legend,
                timestamps: value.timestamps,
            };
            this.getMachineImage();
        },
        async getMachineImage() {
            let toHash = {
                area: this.selectedException.exc.area,
                unitId: this.selectedException.exc.unitId,
                code: this.selectedException.exc.code,
                hwId: this.selectedException.exc.hwId,
                hwType: this.selectedException.exc.hwType,
                model: this.machine.model,
            };
            let excHash = this.hashCode(JSON.stringify(toHash));
            if (!this.cachedImages.has(excHash)) {
                this.loadingImage = true;
                await this[FETCH_EXCEPTION_IMAGE]({
                    installationName: this.installationName,
                    serialNumber: this.machine.serialNumber,
                    exc: this.selectedException.exc,
                    excHash: excHash,
                });
                this.loadingImage = false;
            }
            this.selectedException.image = this.cachedImages.get(excHash);
        },
        hashCode(str: string) {
            let h1 = 0xdeadbeef ^ 0,
                h2 = 0x41c6ce57 ^ 0;
            for (let i = 0, ch; i < str.length; i++) {
                ch = str.charCodeAt(i);
                h1 = Math.imul(h1 ^ ch, 2654435761);
                h2 = Math.imul(h2 ^ ch, 1597334677);
            }
            h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
            h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
            return 4294967296 * (2097151 & h2) + (h1 >>> 0);
        },
        reset() {
            this.seriesMETrendList = [];
            this.exceptionTableData = [];
            this.totalRowExceptionDataTable = 0;
            this.maxYValue = 0;
        },
    },
    watch: {
        machineExceptionList: {
            handler(value) {
                this.reset();
                if (value && value.length) {
                    let machineExceptionTrendMap = new Map<string, Array<number>>();
                    let codes = [...new Set<string>(this.machineExceptionList.map((me) => me.getHexCode()))];
                    codes.forEach((code) => {
                        machineExceptionTrendMap.set(code, new Array(this.nDays).fill(0));
                    });
                    let codeDescription = "-";
                    let excDescription = "-";
                    let excDict = null;
                    let meTableDataMap = new Map<string, number>();
                    let exceptionMapIndexes = new Map<string, number>();
                    this.machineExceptionList.forEach((me, i) => {
                        let utcDate = Date.UTC(me.dateTime.getFullYear(), me.dateTime.getMonth(), me.dateTime.getDate(), 0, 0, 0);
                        let dayIndex = Math.floor((utcDate - this.startDate.getTime()) / (1000 * 3600 * 24));
                        machineExceptionTrendMap.get(me.getHexCode())[dayIndex] += 1;
                        let area = me.unitId <= 0 ? me.area : me.unitId;
                        let key = me.code + "," + area.toString(10) + "," + me.hwId.toString(10);
                        if (!meTableDataMap.has(key)) meTableDataMap.set(key, 0);
                        meTableDataMap.set(key, meTableDataMap.get(key) + 1);
                        this.totalRowExceptionDataTable += 1;
                        exceptionMapIndexes.set(key, i);
                    });
                    meTableDataMap.forEach((value, key) => {
                        let keyArr = key.split(",");
                        excDict = this.troubleshooting[keyArr[0]];
                        if (excDict != null && excDict != undefined) {
                            codeDescription = excDict.title;
                            excDescription = excDict.descr;
                        }
                        let exc = this.machineExceptionList[exceptionMapIndexes.get(key)];
                        let formattedCode = "";
                        if (codeDescription.includes(":")) formattedCode = codeDescription;
                        else formattedCode = exc.getHexCode() + ": " + codeDescription;
                        let timestamps = this.machineExceptionList
                            .filter((me) => me.code == keyArr[0] && (me.area == keyArr[1] || me.unitId == keyArr[1]) && me.hwId == keyArr[2])
                            .map((me) => me.dateTime);
                        this.exceptionTableData.push({
                            code: formattedCode,
                            area: this.unitLabelMap[keyArr[1]],
                            component: this.componentLabelMap[exc.hwType + "," + exc.hwId],
                            description: excDescription,
                            number: value,
                            percentage: ((value * 100) / this.totalRowExceptionDataTable).toFixed(0) + "%",
                            troub: excDict && excDict.troub ? excDict.troub : "",
                            exc: exc,
                            legend: this.imagesLegend.get(exc.id.toString()),
                            timestamps: timestamps,
                        });
                    });
                    let maxValue = Array(this.nDays).fill(0);
                    machineExceptionTrendMap.forEach((value, key) => {
                        let keyLabel = this.troubleshooting[parseInt(key, 16)].title;
                        value.forEach((element, i) => {
                            maxValue[i] += element;
                        });
                        if (!value.every((element) => element === 0)) {
                            this.seriesMETrendList.push({ name: keyLabel && keyLabel.includes(":") ? keyLabel.split(":")[0] : key, data: value });
                        }
                    });
                    this.maxYValue = Math.max(...maxValue);
                }
            },
            immediate: true,
            deep: true,
        },
    },
});
